import { SunMoon } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { BrandButton } from "./brand/BrandButton";
import { useTranslation } from "react-i18next";
import {
  THEME_LABEL_KEYS,
  THEME_PREFERENCES,
  usePreferencesStore,
} from "../store/preferences-store";

/**
 * ThemeSwitcher Component
 * Dropdown menu for choosing the appearance — mirrors `LanguageSwitcher`,
 * beside which it sits in the Header (spec 2026-09-07 D18).
 *
 * Features:
 * - Three states: follow this device, light, dark
 * - Marks the current state
 * - Persists the choice per device (`usePreferencesStore`)
 * - `MainLayout` repaints the running app from the same store
 */
export function ThemeSwitcher() {
  const { t } = useTranslation();
  const theme = usePreferencesStore((s) => s.theme);
  const setTheme = usePreferencesStore((s) => s.setTheme);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <BrandButton
          variant="ghost"
          size="icon"
          className="text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
          aria-label={t("theme.change")}
        >
          <SunMoon className="h-5 w-5" />
        </BrandButton>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="w-48 bg-background border-border shadow-lg"
      >
        <DropdownMenuLabel className="text-foreground">
          {t("theme.label")}
        </DropdownMenuLabel>
        <DropdownMenuSeparator className="bg-border" />
        {THEME_PREFERENCES.map((option) => (
          <DropdownMenuItem
            key={option}
            onClick={() => setTheme(option)}
            className={`
              cursor-pointer
              ${
                theme === option
                  ? "bg-primary/10 text-foreground border-l-2 border-border"
                  : "text-muted-foreground hover:text-foreground"
              }
              hover:bg-secondary focus:bg-secondary
            `}
          >
            <span className="flex items-center gap-2">
              {t(THEME_LABEL_KEYS[option])}
              {theme === option && (
                <span className="ml-auto text-primary">✓</span>
              )}
            </span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
